// src/utils/pendingTrialSignup.js
// A parent who submits /trial with an email that already has an account can't
// create a new Auth account, so TrialSignup stashes the createTrialSignup
// payload here and sends them to /login. Login resumes it once; on failure it
// hands off to /trial-status, which shows a "try again" card. This module is the
// single owner of that sessionStorage handoff.
//
// The stash records which email it was built for: only the account it belongs
// to may resume it (a shared browser could otherwise attach one parent's trial
// to another's account).

const KEY = 'trial_signup_pending';
const ATTEMPTS_KEY = 'trial_signup_pending_attempts';

// After this many failed resume attempts (Login's one attempt + retries on
// /trial-status), stop retrying and tell the parent to contact us.
export const MAX_RESUME_ATTEMPTS = 3;

const norm = (e) => (e || '').trim().toLowerCase();

export function stashPendingTrialSignup(email, payload) {
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ email: norm(email), payload }));
    sessionStorage.removeItem(ATTEMPTS_KEY);
  } catch {
    /* private mode — best effort */
  }
}

// Returns { payload, attempts } when there is a stash for `currentEmail`, else
// null. A stash for a different email is left untouched (its owner may still
// return in this tab).
export function readPendingTrialSignup(currentEmail) {
  let raw = null;
  try { raw = sessionStorage.getItem(KEY); } catch { return null; }
  if (!raw) return null;

  let parsed;
  try { parsed = JSON.parse(raw); } catch { clearPendingTrialSignup(); return null; }
  if (!parsed || !parsed.payload) { clearPendingTrialSignup(); return null; }
  if (currentEmail && parsed.email && parsed.email !== norm(currentEmail)) return null;

  let attempts = 0;
  try { attempts = parseInt(sessionStorage.getItem(ATTEMPTS_KEY) || '0', 10) || 0; } catch { attempts = 0; }
  return { payload: parsed.payload, attempts };
}

// Increment and return the attempt count. Call immediately before each
// createTrialSignup resume call.
export function recordPendingTrialSignupAttempt() {
  let attempts = 0;
  try { attempts = parseInt(sessionStorage.getItem(ATTEMPTS_KEY) || '0', 10) || 0; } catch { attempts = 0; }
  attempts += 1;
  try { sessionStorage.setItem(ATTEMPTS_KEY, String(attempts)); } catch { /* ignore */ }
  return attempts;
}

export function clearPendingTrialSignup() {
  try { sessionStorage.removeItem(KEY); } catch { /* ignore */ }
  try { sessionStorage.removeItem(ATTEMPTS_KEY); } catch { /* ignore */ }
}
